import React from 'react';
import { Box, Typography, Popover, Chip } from '@mui/material';
import { FaArrowLeft } from 'react-icons/fa';
import { COLORS } from '../../../../../../constants/radar';
import ReviewerCompetencyList from './ReviewerCompetencyList';
import SkillLevelSelector from '../../../../../TrendRadarChart/components/TreeChart/components/SkillLevelSelector';

const SkillAssessmentStep = ({
  activeStep,
  myReviewer,
  currentSkillTemplate,
  loadingSkillTemplate,
  skillLevels,
  defaultLevelId,
  assignment,
  competencies,
  employeeId,
  visualizationType,
  handleOverallLevelSelectWithClose,
  // ADR-014 §7: parent session closed (snapshot close) => competency selectors
  // and comment fields render non-interactive (the API also rejects with 409).
  sessionLocked = false,
  handleOpenSelector,
  handleCommentChange,
  loadingSegmentMap,
  loadingOverallSkill,
  followUpQuestions,
  handleLevelSelect,
  handleSegmentCommentChange,
  anchorEl,
  handleCloseSelector,
  activeMapping,
  getSegmentLevel,
  handleSegmentLevelSelectWithClose,
  overallCompetencyAnchor,
  setOverallCompetencyAnchor,
  getOverallLevel,
  isDirty,
  handleStep,
  isStepComplete,
  getSkillLevelsBySegmentId,
  getCustomDescriptionsBySegmentId,
  getCustomDescriptionsBySkill,
  hideFollowUpQuestions = false
}) => {
  const skillTemplate = myReviewer?.skillsMatrixTemplate?.skillsTemplates[activeStep - 1];
  
  if (!skillTemplate) return null;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Box sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: COLORS.PRIMARY,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 2,
          fontSize: '16px',
          fontWeight: 500
        }}>
          {activeStep}
        </Box>
        <Typography variant="h6" sx={{ color: COLORS.PRIMARY }}>
          {skillTemplate.skill.name}
        </Typography>
        {skillTemplate.isRequired === false && (
          <Chip
            label="Optional"
            size="small"
            sx={{
              ml: 2,
              backgroundColor: '#FFF3E0',
              color: '#E65100',
              fontWeight: 500
            }}
          />
        )}
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Description
        </Typography>
        <Typography variant="body1">
          {skillTemplate.skill.description}
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
          Overall Competency for {skillTemplate.skill.name}
        </Typography>

        <ReviewerCompetencyList
          skillId={skillTemplate.id}
          segmentId={null}
          competencies={competencies}
          employeeId={employeeId}
          visualizationType={visualizationType}
          isInteractive={!sessionLocked}
          skillName={skillTemplate.skill.name}
          customDescriptions={getCustomDescriptionsBySkill()}
          isLoadingDescriptions={loadingSkillTemplate}
          onLevelSelect={handleOverallLevelSelectWithClose}
          onDropdownClick={handleOpenSelector}
          skillLevels={skillLevels}
          commentsAllowed={true}
          commentsRequired={skillTemplate.isRequired !== false}
          onCommentChange={handleCommentChange}
          loadingSegmentMap={loadingSegmentMap}
          loadingOverallSkill={loadingOverallSkill}
          followUpQuestions={followUpQuestions}
          assignment={assignment}
          hasMappings={skillTemplate.mappings?.length > 0}
        />
      </Box>

      {skillTemplate.mappings?.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Assessed Skills
          </Typography>
          {skillTemplate.mappings?.map((mapping) => (
            <Box 
              key={mapping.id} 
              sx={{ 
                mb: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {mapping.segment.segment.name}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontStyle: mapping.segment.segment.description ? 'normal' : 'italic'
                  }}
                >
                  {mapping.segment.segment.description}
                </Typography>
              </Box>

              <ReviewerCompetencyList
                skillId={skillTemplate.id}
                segmentId={mapping.segment.id}
                competencies={competencies}
                employeeId={employeeId}
                visualizationType={visualizationType}
                isInteractive={!sessionLocked}
                skillName={mapping.segment.segment.name}
                customDescriptions={getCustomDescriptionsBySegmentId(mapping.segment.id)}
                isLoadingDescriptions={loadingSkillTemplate}
                onLevelSelect={handleLevelSelect}
                onDropdownClick={handleOpenSelector}
                skillLevels={getSkillLevelsBySegmentId(mapping.segment.id)}
                commentsAllowed={true}
                commentsRequired={skillTemplate.isRequired !== false}
                onCommentChange={(reviewerId, skillId, value) =>
                  handleSegmentCommentChange(reviewerId, skillId, mapping.segment.id, mapping.id, value)
                }
                loadingSegmentMap={loadingSegmentMap}
                loadingOverallSkill={loadingOverallSkill}
                assignment={assignment}
              />
            </Box>

          ))}
        </Box>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseSelector}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {activeMapping && (
          <SkillLevelSelector
            selectedLevelId={getSegmentLevel(activeStep - 1, activeMapping.segment.id) || defaultLevelId}
            onLevelSelect={handleSegmentLevelSelectWithClose(activeMapping, activeStep)}
            skillName={myReviewer?.skillsMatrixTemplate?.skillsTemplates[activeStep - 1].mappings.find(m => m.id === activeMapping.id)?.segment.segment.name}
            showHeader={false}
            customDescriptions={getCustomDescriptionsBySegmentId(activeMapping.segment.id)}
            isLoadingDescriptions={loadingSkillTemplate}
            skillLevels={getSkillLevelsBySegmentId(activeMapping.segment.id)}
            loading={loadingSegmentMap[`${myReviewer?.id}-${activeMapping.segment.id}`]}
          />
        )}
      </Popover>

      <Popover
        open={Boolean(overallCompetencyAnchor)}
        anchorEl={overallCompetencyAnchor}
        onClose={() => setOverallCompetencyAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <SkillLevelSelector
          selectedLevelId={getOverallLevel(activeStep - 1) || defaultLevelId}
          onLevelSelect={(level) => handleOverallLevelSelectWithClose(myReviewer, skillTemplate.id, null, level)}
          skillName={skillTemplate.skill.name}
          showHeader={false}
          customDescriptions={getCustomDescriptionsBySkill()}
          isLoadingDescriptions={loadingSkillTemplate}
          skillLevels={skillLevels}
          loading={loadingOverallSkill[`${myReviewer?.id}-${skillTemplate.id}`]}
        />
      </Popover>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        mt: 4,
        pt: 3,
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Box
          onClick={() => activeStep > 1 && !isDirty && handleStep(activeStep - 1, isDirty)()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: activeStep > 1 && !isDirty ? COLORS.PRIMARY : 'text.disabled',
            cursor: activeStep > 1 && !isDirty ? 'pointer' : 'not-allowed',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              opacity: activeStep > 1 && !isDirty ? 0.8 : 1
            }
          }}
        >
          <FaArrowLeft style={{ marginRight: '8px' }} /> Previous
        </Box>
        <Box
          onClick={() => {
            if (isDirty) return;
            const maxSteps = myReviewer?.skillsMatrixTemplate?.skillsTemplates.length + 1;
            
            if (isStepComplete(activeStep) && activeStep <= maxSteps) {
              handleStep(activeStep + 1, isDirty)();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: isStepComplete(activeStep) && activeStep <= myReviewer?.skillsMatrixTemplate?.skillsTemplates.length && !isDirty
              ? COLORS.PRIMARY 
              : 'text.disabled',
            cursor: isStepComplete(activeStep) && activeStep <= myReviewer?.skillsMatrixTemplate?.skillsTemplates.length && !isDirty
              ? 'pointer' 
              : 'not-allowed',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              opacity: isStepComplete(activeStep) && activeStep <= myReviewer?.skillsMatrixTemplate?.skillsTemplates.length && !isDirty ? 0.8 : 1
            }
          }}
        >
          Next <FaArrowLeft style={{ marginLeft: '8px', transform: 'rotate(180deg)' }} />
        </Box>
      </Box>
    </>
  );
};

export default SkillAssessmentStep; 